"use client";

// One-shot client boot: read URL params (election_id, phone, magic_token) once,
// init locale + App Check, wire the 401 handler, then bootstrap auth.
// Params can live in the query OR the hash (legacy Flutter hash-router links).

import { useEffect } from "react";
import { useApp } from "@/store/appStore";
import { initAppCheck } from "@/lib/firebase";
import { setUnauthorizedHandler } from "@/lib/api";
import { startAutoUpdate } from "@/lib/autoUpdate";

function readParams(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  const add = (qs: string) => {
    new URLSearchParams(qs).forEach((v, k) => {
      if (v) out[k] = v;
    });
  };
  add(window.location.search.replace(/^\?/, ""));
  const hash = window.location.hash.replace(/^#/, "");
  const qIdx = hash.indexOf("?");
  if (qIdx !== -1) add(hash.slice(qIdx + 1));
  return out;
}

export default function ClientBoot() {
  const initLocale = useApp((s) => s.initLocale);
  const setElectionId = useApp((s) => s.setElectionId);
  const bootstrap = useApp((s) => s.bootstrap);

  // Mirrors Flutter's AppLifecycleObserver: on "app resumed" (here, the tab
  // becoming visible again — e.g. voter left the browser open and came back),
  // silently refresh the backend token from the still-valid Firebase session
  // instead of waiting to fail on the next real request.
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      // refreshSession owns the guards and the network-vs-auth error split, so
      // resuming the tab and recovering from a 401 stay one behaviour. The old
      // inline version swallowed auth failures too, leaving a dead session that
      // only blew up later on the voter's real request.
      void useApp.getState().refreshSession();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Picks up a new deploy without anyone being told to refresh.
  useEffect(() => startAutoUpdate(), []);

  useEffect(() => {
    // Static export has no server to print browser errors like `next dev`
    // does — tag every uncaught error/rejection clearly so it's findable in
    // production DevTools (paired with sourcemaps, the stack is de-minified).
    window.addEventListener("error", (e) => {
      console.error("[uncaught]", e.error ?? e.message);
    });
    window.addEventListener("unhandledrejection", (e) => {
      console.error("[unhandled rejection]", e.reason);
    });

    initLocale();
    void initAppCheck();
    // A 401 must try to renew the bearer, NOT sign out: signOut() clears the
    // saved candidate selection, so an expired token mid-ballot used to throw
    // the voter back to /login with their choices gone.
    setUnauthorizedHandler(() => void useApp.getState().refreshSession());

    const p = readParams();
    // election_id: URL param wins, else the value persisted this session (so a
    // refresh on /candidates keeps its election instead of bouncing to the picker).
    const persistedElectionId =
      typeof window !== "undefined"
        ? window.localStorage.getItem("election_id")
        : null;
    const electionId = p.election_id || persistedElectionId;
    if (electionId) setElectionId(electionId);

    void bootstrap({ phone: p.phone, magicToken: p.magic_token });

    // Scrub impersonation credentials from the URL so the magic_token doesn't
    // linger in the address bar / history / referrer and can't be re-shared.
    if ((p.phone || p.magic_token) && typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    // run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
